import { useMemo, useState } from "react";
import type { PersonActivityItem, PersonProfile } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";
import {
  useLogPersonActivity,
  usePersonActivities,
} from "@/hooks/usePerson";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "email", label: "Emails" },
  { id: "call", label: "Calls" },
  { id: "meeting", label: "Meetings" },
  { id: "note", label: "Notes" },
  { id: "task", label: "Tasks" },
  { id: "activity_log", label: "Activity log" },
] as const;

export function PersonActivitiesTab({
  personId,
  profile,
}: {
  personId: string;
  profile: PersonProfile;
}) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("all");
  const [logOpen, setLogOpen] = useState(false);
  const [summary, setSummary] = useState("");
  const [logType, setLogType] = useState<"note" | "call" | "meeting" | "email">(
    "note",
  );

  const types = filter === "all" ? undefined : [filter];
  const { data, isLoading, error } = usePersonActivities(personId, types);
  const logMutation = useLogPersonActivity(personId);

  const activities = data?.activities ?? [];

  const grouped = useMemo(() => {
    const map = new Map<string, PersonActivityItem[]>();
    for (const a of activities) {
      const d = new Date(a.occurredAt);
      const key = Number.isNaN(d.getTime())
        ? "Unknown"
        : d.toLocaleString(undefined, { month: "long", year: "numeric" });
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return [...map.entries()];
  }, [activities]);

  async function handleLog() {
    if (!summary.trim()) return;
    await logMutation.mutateAsync({
      type: logType,
      summary: summary.trim(),
      leadId: profile.leadId,
      accountId: profile.accountId,
    });
    setSummary("");
    setLogOpen(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs font-medium",
                filter === f.id
                  ? "bg-brand text-brand-foreground"
                  : "border border-border text-muted-foreground hover:bg-muted/40",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setLogOpen((v) => !v)}
          className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
        >
          Log activity
        </button>
      </div>

      {logOpen ? (
        <div className="space-y-2 rounded-lg border border-border bg-card p-3">
          <div className="flex flex-wrap gap-2">
            {(["note", "call", "meeting", "email"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setLogType(t)}
                className={cn(
                  "rounded-md px-2 py-1 text-xs capitalize",
                  logType === t
                    ? "bg-muted font-semibold"
                    : "text-muted-foreground hover:bg-muted/40",
                )}
              >
                {t}
              </button>
            ))}
          </div>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            placeholder="What happened?"
            className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setLogOpen(false)}
              className="rounded-md px-2.5 py-1.5 text-xs text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!summary.trim() || logMutation.isPending}
              onClick={() => void handleLog()}
              className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
            >
              {logMutation.isPending ? "Saving…" : "Save"}
            </button>
          </div>
          {!profile.leadId ? (
            <p className="text-[11px] text-muted-foreground">
              Save this person to leads to attach future activity to a CRM
              contact id.
            </p>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          Loading activities…
        </p>
      ) : error ? (
        <p className="py-8 text-center text-sm text-destructive">
          Failed to load activities.
        </p>
      ) : activities.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center">
          <p className="text-sm font-medium">No activities found</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Log a note, email, or call to start this contact&apos;s timeline.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([month, items]) => (
            <div key={month}>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {month}
              </h4>
              <ul className="space-y-2">
                {items.map((a) => (
                  <li
                    key={a.id}
                    className="min-w-0 overflow-hidden rounded-lg border border-border bg-card px-3 py-2.5"
                  >
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {a.type.replace("_", " ")}
                        </p>
                        <p className="break-words text-sm font-medium text-foreground">
                          {a.title}
                        </p>
                        {a.body ? (
                          <p className="mt-0.5 break-words text-xs text-muted-foreground">
                            {a.body}
                          </p>
                        ) : null}
                      </div>
                      <time className="shrink-0 text-[11px] text-muted-foreground">
                        {new Date(a.occurredAt).toLocaleString()}
                      </time>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
